/**
 * Voice Recognition Helper
 *
 * Provides voice recognition functionality for the chatbot interface
 */

export interface VoiceRecognitionCallbacks {
  onStart: () => void;
  onResult: (transcript: string) => void;
  onError: (error: string) => void;
  onEnd: () => void;
}

/**
 * Start voice recording using the Web Speech API
 *
 * @param {VoiceRecognitionCallbacks} callbacks - Object containing callback functions for recognition events
 * @return {any | null} SpeechRecognition instance or null if not supported
 */
export const startVoiceRecording = (callbacks: VoiceRecognitionCallbacks): any | null => {
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

  if (!SpeechRecognition) {
    alert('Voice recognition is not supported in your browser. Please try Chrome, Edge, or Safari.');
    return null;
  }

  const recognitionInstance = new SpeechRecognition();
  // The browser will attempt to detect the language automatically based on the user's input
  recognitionInstance.lang = '';
  recognitionInstance.interimResults = true;
  recognitionInstance.maxAlternatives = 1;

  recognitionInstance.onstart = () => {
    callbacks.onStart();
  };

  recognitionInstance.onresult = (event: any) => {
    const transcript = event.results[0][0].transcript;
    callbacks.onResult(transcript);
  };

  recognitionInstance.onerror = (event: any) => {
    console.error('Speech recognition error:', event.error);
    callbacks.onError(event.error);
    if (event.error === 'not-allowed') {
      alert('Microphone access denied. Please allow microphone access in your browser settings.');
    }
  };

  recognitionInstance.onend = () => {
    callbacks.onEnd();
  };

  recognitionInstance.start();

  return recognitionInstance;
};

/**
 * Stop an active voice recording session
 *
 * @param {any | null} recognition - The SpeechRecognition instance to stop
 * @return {void}
 */
export const stopVoiceRecording = (recognition: any | null): void => {
  if (recognition) {
    recognition.stop();
  }
};
